import { useEffect, useState } from "react";
import { Col, Row } from "@/utils/bootstrap";
import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import { useManageCartItem } from "@/api/AppApi/services/cart";
import {
  ICartItem_Product,
  IGetCart,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import { debounce, getItemCartPropsToPost_product } from "../../cart-functions";
import CartItemIcons from "./cart-item-icons/CartItemIcons";
import ItemQuantityBtn from "./cart-item-btns/item-quantity-btn/ItemQuantityBtn";
import Price from "@/app/components/lib/Price";
import { currencyAtom } from "@/hooks/currencyAtom";
import { useAtom } from "jotai";
import CartItemImg from "./CartItemImg";
import { useTranslation } from "@/hooks/localization/useTranslations";
import CartItemUnitDropdown from "./cart-item-untits-dropdown";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { EventType } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import ProductPriceInfoButton from "@/app/components/common-builder/product/product-box/product-box-helpers/product-price-info/ProductPriceInfoButton";
import calculatPricesForSpecialPrices from "@/utils/functions/calculatPriceForSpecialPrices";
import NotFieldErrModal from "@/app/components/common-form-builder/form-builder-helpers/NotFieldErrModal";
import { useApiErrorPopup } from "@/hooks/useApiErrorPopup";
import BundleItems from "./bundle/BundleItems";
import { v4 as uuidv4 } from "uuid";
import { useCallback, useRef } from "react";

interface Props {
  cartItem: ICartItem_Product;
  mobile?: boolean;
  menu?: boolean;
}

const CartItemProduct: React.FC<Props> = ({ cartItem, mobile, menu }) => {
  const FBEventIDRef = useRef<string>("");
  const t = useTranslation();
  const { eventTrackers } = useEventTrackers();
  const { data: CompanyData } = useGetCompanyQuery();
  const show_all_product_units =
    CompanyData?.result?.theme?.components?.product?.show_all_product_units ||
    false;
  const default_product_unit_price =
    CompanyData?.result?.theme?.components?.product?.default_product_unit_price;
  const enable_multiple_unit_add_to_cart =
    CompanyData?.result?.theme?.components?.cart?.enable_multiple_unit_add_to_cart;

  const {
    notFieldErr,
    setNotFieldErr,
    errorRef,
    report: reportCartError,
  } = useApiErrorPopup("update_cart_item_quantity");
  const [currency] = useAtom(currencyAtom);
  const [quantity, setQuantity] = useState(0);
  const [unitId, setUnitId] = useState<number>(cartItem.others.unit_id);
  const selectedUnit = cartItem.others.units.find((item) => item.id === unitId);
  const small = (mobile || menu) ?? false;
  const specialPrices = cartItem.others.selling_prices;

  // Only the unit is taken from the client-side special-price match (it is posted on a
  // quantity change). The DISPLAYED price always comes from the cart item: the backend
  // already applied the right quantity tier, whereas re-deriving it here picks the first
  // tier in `default_product_unit_price` order — with `highest_price` that is the
  // "2 and more" tier for a quantity of 3, showing 420 instead of the charged 410.
  const { unit_id: calculated_unit_id } = calculatPricesForSpecialPrices({
    originalPrice: cartItem.price,
    originalPriceWithoutDiscount: cartItem.basic_price,
    unitId,
    defaultProductUnitPrice: default_product_unit_price,
  })({
    quantity,
    specialPrices,
  });

  const manageCartApi = useManageCartItem({
    onSuccess: (res: IGetCart, variables) => {
      const { coupon } = res.result ?? {};
      if (res?.message?.type === ApiMsgTypes.success) {
        eventTrackers(EventType.ADD_CART, [{ ...cartItem }], {
          currency: currency?.code,
          value: cartItem?.price,
          coupon: coupon?.code ?? "",
          discount: cartItem?.discount,
        }, {}, variables.event_id);
      }
    },
    onError: (error: any) => {
      setQuantity(cartItem?.quantity ?? 0);
      reportCartError(error, {
        product_id: cartItem?.inner_id ?? null,
        cart_item_id: cartItem?.id ?? null,
        quantity: cartItem?.quantity ?? null,
      });
    },
  });
  const debounceMutate = useCallback(
    debounce((params: any) => manageCartApi.mutate(params), 500),
    [],
  );

  const handleQuantityChange = (qty: number) => {
    FBEventIDRef.current = uuidv4();
    setQuantity(qty);
    debounceMutate({
      ...getItemCartPropsToPost_product(cartItem),
      unit_id: calculated_unit_id ?? unitId,
      quantity: qty,
      event_id: FBEventIDRef.current,
    });
  };
  const handleUnitChange = (unitId: number) => {
    setUnitId(unitId);
    let quantity =
      selectedUnit?.min_quantity_in_cart &&
      selectedUnit?.min_quantity_in_cart > 0 &&
      cartItem.quantity < selectedUnit?.min_quantity_in_cart
        ? selectedUnit?.min_quantity_in_cart
        : cartItem.quantity;
    quantity =
      selectedUnit?.max_quantity_in_cart &&
      selectedUnit?.max_quantity_in_cart > 0 &&
      cartItem.quantity > selectedUnit?.max_quantity_in_cart
        ? selectedUnit?.max_quantity_in_cart
        : cartItem.quantity;
    FBEventIDRef.current = uuidv4();
    debounceMutate({
      ...getItemCartPropsToPost_product(cartItem),
      quantity,
      unit_id: unitId,
      event_id: FBEventIDRef.current,
    });
  };

  const debounceNote = useCallback(
    debounce((params: any) => manageCartApi.mutate(params), 1000),
    [],
  );

  const handleNoteChange = (note: string) => {
    FBEventIDRef.current = uuidv4();
    debounceNote({
      ...getItemCartPropsToPost_product(cartItem),
      note: note,
      event_id: FBEventIDRef.current,
    });
  };
  useEffect(() => {
    setQuantity(cartItem.quantity);
    setUnitId(cartItem.others.unit_id);
  }, [cartItem.quantity, cartItem.others.unit_id]);
  const maxQuantity = Math.min(
    cartItem.others?.quantity_on_hand,
    selectedUnit?.max_quantity_in_cart && selectedUnit?.max_quantity_in_cart > 0
      ? selectedUnit?.max_quantity_in_cart
      : (cartItem?.others.max_quantity_in_cart ?? 999),
  );
  return (
    <>
      {notFieldErr.length > 0 && (
        <NotFieldErrModal
          errors={notFieldErr}
          setErrors={setNotFieldErr}
          errorRef={errorRef}
        />
      )}
      <Row className="m-0 cart-item">
        <CartItemImg
          image_url={cartItem?.thumbnail_url}
          is_new={cartItem?.others?.is_new}
          discount_amount={cartItem?.others?.discount_amount}
          outOfStock={cartItem.others.quantity_on_hand <= 0}
          has_discount={cartItem?.others?.discount_amount > 0}
          discount_type={cartItem?.others?.discount_type}
          discount={cartItem?.others?.discount}
        />
        <Col className="p-0">
          <Row className="m-0">
            <Col className="p-0">
              <h3 className="name m-0">{cartItem?.name}</h3>
              {cartItem?.others.attrs_values.length !== 0 && (
                <p className="desc m-0">
                  {cartItem?.others.attrs_values
                    .map((attr) => attr.name)
                    .join(", ")}
                </p>
              )}
              {enable_multiple_unit_add_to_cart && selectedUnit && (
                <p className="desc m-0">
                  {selectedUnit.name}
                </p>
              )}
              <div className="price-line">
                <Price
                  priceFirst
                  gap={12}
                  price={{ value: cartItem.price, className: "price" }}
                  priceWithoutDiscount={{
                    value: cartItem.basic_price,
                    className: "price-without-discount",
                  }}
                  sameLine={true}
                />
                {!!(specialPrices || []).length && (
                  <ProductPriceInfoButton priceInfoData={specialPrices} />
                )}
              </div>
            </Col>
            <Col className="p-0 col-auto">
              <CartItemIcons
                small={small}
                cartItem={cartItem}
                setNote={handleNoteChange}
              />
            </Col>
          </Row>
          <Row className="m-0 align-items-end">
            <Col className="p-0">
              {cartItem?.addons?.length > 0 && (
                <div className="item-add-ons">
                  {!menu ? (
                    cartItem?.addons?.map((addon, key) => (
                      <div
                        key={key}
                      >{`${addon.name} (x${addon.quantity})`}</div>
                    ))
                  ) : (
                    <>{`${t.addons}: ${cartItem?.addons.length}`}</>
                  )}
                </div>
              )}
              {cartItem?.free_quantity > 0 && (
                <Row className="m-0 note">{`${t.free_quantity}: ${cartItem?.free_quantity}`}</Row>
              )}
              <Row className="m-0 note">{`${t.notes}: ${cartItem?.note}`}</Row>
            </Col>
            <BottomLayout condition={!!cartItem.others.units?.length}>
              <Col className="p-0 col-auto">
                <ItemQuantityBtn
                  cartItemId={cartItem?.id}
                  quantity={quantity}
                  setQuantity={handleQuantityChange}
                  plusDisabled={quantity >= maxQuantity}
                  maxQuantity={maxQuantity}
                  min_quantity_in_cart={
                    selectedUnit?.min_quantity_in_cart &&
                    selectedUnit?.min_quantity_in_cart > 0
                      ? selectedUnit?.min_quantity_in_cart
                      : cartItem.others.min_quantity_in_cart
                  }
                />
              </Col>
              {show_all_product_units && cartItem.others.units?.length && !enable_multiple_unit_add_to_cart ? (
                <Col className="p-0 col-6">
                  <CartItemUnitDropdown
                    onSelect={handleUnitChange}
                    selectedId={unitId as number}
                    units={cartItem.others.units}
                  />
                </Col>
              ) : (
                <></>
              )}
            </BottomLayout>
          </Row>
        </Col>
        {!!cartItem?.bundle_items_details?.length && (
          <Col className="col-12">
            <BundleItems items={cartItem?.bundle_items_details} />
          </Col>
        )}
      </Row>
    </>
  );
};

export default CartItemProduct;

const BottomLayout: React.FC<{
  children: React.ReactElement[];
  condition: boolean;
}> = ({ children, condition }) => {
  if (condition) {
    return <Row className="flex-nowrap gap-2 p-0">{children}</Row>;
  }
  return children;
};
